import { useMemo, useState } from 'react';
import { RoseChart, RoseLegend, STATUS_LABELS, type RoseFamily } from '../../components/RoseChart';
import { Empty, ErrorBanner, Loading } from '../../components/ui';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  AmComplianceSummary,
  ComplianceAssessment,
  ResultStatus,
} from '../../lib/types';

/**
 * A client's compliance posture, read from inside account management.
 *
 * READ-ONLY, DELIBERATELY. The assessment is worked in the compliance module,
 * by whoever owns the framework; this is the account manager's window onto it
 * while they are looking at the client or preparing a review. Putting the
 * status buttons here would mean an account manager could quietly change a
 * compliance record from a screen whose whole purpose is to read it.
 *
 * The gaps list is what the tab is really for. A score tells you whether to
 * worry; "these eleven are not met, and here they are" is what you take into
 * a conversation with the client.
 */
export function AmCompliance({
  clientId,
  assessments,
}: {
  clientId: string;
  assessments: AmComplianceSummary[];
}) {
  const [chosen, setChosen] = useState<number | null>(null);

  /* Defaults to the first, which is framework sort order — the same order the
     compliance module lists them in, so the two screens agree on which is the
     client's headline framework. */
  const active = chosen ?? assessments[0]?.AssessmentId ?? null;

  if (assessments.length === 0) {
    return (
      <Empty title="No compliance assessments">
        <p>
          Nothing is being assessed for this client yet. Assessments are started in the Compliance
          module, and appear here once they exist.
        </p>
      </Empty>
    );
  }

  return (
    <>
      {/* Only when there is a choice to make. One assessment needs no picker,
          and a single-option switch reads as though something is missing. */}
      {assessments.length > 1 && (
        <div className="tabs mb-4">
          {assessments.map((a) => (
            <button
              key={a.AssessmentId}
              type="button"
              className={active === a.AssessmentId ? 'on' : ''}
              aria-pressed={active === a.AssessmentId}
              onClick={() => setChosen(a.AssessmentId)}
            >
              {a.FrameworkShortName ?? a.FrameworkCode}
              <span className="xs subtle"> {a.ScorePct === null ? '—' : `${a.ScorePct}%`}</span>
            </button>
          ))}
        </div>
      )}

      {active !== null && <ComplianceReport clientId={clientId} assessmentId={active} />}
    </>
  );
}

/** Met against what applies. Partial does not count as half — see the API. */
function pctOf(tally: Record<ResultStatus, number>): number | null {
  const applicable =
    tally.met + tally.partial + tally.not_met + tally.not_assessed;
  return applicable > 0 ? Math.round((tally.met / applicable) * 100) : null;
}

export function ComplianceReport({
  clientId,
  assessmentId,
}: {
  clientId: string;
  assessmentId: number;
}) {
  const resource = useResource<ComplianceAssessment>(
    `/am/clients/${clientId}/compliance/${assessmentId}`,
  );
  const data = resource.data;

  /* Tallied here from the requirements the payload already carries, the same
     way the compliance workspace does it — one shape of the numbers, computed
     from the same rows, so the two screens cannot disagree. */
  const view = useMemo(() => {
    if (!data) return null;

    const byFamily = new Map<number, typeof data.requirements>();
    for (const r of data.requirements) {
      const list = byFamily.get(r.FamilyId);
      if (list) list.push(r);
      else byFamily.set(r.FamilyId, [r]);
    }

    const blank = (): Record<ResultStatus, number> => ({
      met: 0,
      partial: 0,
      not_met: 0,
      not_applicable: 0,
      not_assessed: 0,
    });

    const families = data.families.map((f) => {
      const items = byFamily.get(f.FamilyId) ?? [];
      const tally = blank();
      for (const r of items) tally[r.Status] += 1;
      return { ...f, items, tally, pct: pctOf(tally) };
    });

    const total = blank();
    for (const r of data.requirements) total[r.Status] += 1;

    /* What you actually talk about: not met first, then partial. Not-assessed
       is left out — "we have not looked at it" is a project-status problem,
       not a finding, and mixing the two makes the list unreadable. */
    const gaps = data.requirements.filter((r) => r.Status === 'not_met' || r.Status === 'partial');

    return { families, total, gaps, overallPct: pctOf(total) };
  }, [data]);

  if (resource.loading && !data) return <Loading label="Loading the assessment" />;
  if (resource.error && !data) return <ErrorBanner message={resource.error} />;
  if (!data || !view) return null;

  const roseFamilies: RoseFamily[] = view.families.map((f) => ({
    FamilyId: f.FamilyId,
    Code: f.Code,
    Title: f.Title,
    cells: f.items.map((r) => ({
      RequirementId: r.RequirementId,
      Code: r.Code,
      Title: r.Title,
      Status: r.Status,
    })),
  }));

  return (
    <>
      <section className="card mb-4">
        <div className="card-head row-between wrap">
          <div className="grow">
            <span className="eyebrow">{data.Category}</span>
            <h2>{data.FrameworkName}</h2>
            <p className="sub">
              {data.ScopeLevelLabel ?? data.ScopeLevelKey}
              {data.OwnerName && ` · owned by ${data.OwnerName}`}
              {data.Status === 'complete' && data.CompletedOn
                ? ` · completed ${formatDate(data.CompletedOn)}`
                : ` · started ${formatDate(data.StartedOn)}`}
            </p>
          </div>
          <span className={`badge ${data.Status === 'complete' ? 'badge-good' : 'badge-neutral'}`}>
            {data.Status === 'complete' ? 'Complete' : 'In progress'}
          </span>
        </div>

        <div className="card-body">
          <RoseChart families={roseFamilies} overallPct={view.overallPct} />
          <RoseLegend tally={view.total} />
          <p className="xs subtle mt-4">
            Worked in the Compliance module — this is a read-only view of it.
          </p>
        </div>
      </section>

      <section className="card mb-4">
        <div className="card-head">
          <div className="grow">
            <h3>By control family</h3>
          </div>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Family</th>
                <th style={{ width: 180 }}>Met</th>
                <th style={{ width: 90 }}>Score</th>
              </tr>
            </thead>
            <tbody>
              {view.families.map((f) => (
                <tr key={f.FamilyId}>
                  <td>
                    <div className="strong small">{f.Code}</div>
                    <div className="xs muted">{f.Title}</div>
                  </td>
                  <td>
                    <div className="meter">
                      <span
                        className={f.pct !== null && f.pct < 50 ? 'low' : ''}
                        style={{ width: `${f.pct ?? 0}%` }}
                      />
                    </div>
                    <span className="xs muted mono">
                      {f.tally.met}/{f.items.length - f.tally.not_applicable} applicable
                    </span>
                  </td>
                  <td className="mono">{f.pct === null ? '—' : `${f.pct}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="card-head row-between wrap">
          <div className="grow">
            <h3>Gaps</h3>
            <p className="xs subtle">
              What is not met or only partly met — the list worth taking into a review.
            </p>
          </div>
          <span className={`badge ${view.gaps.length === 0 ? 'badge-good' : 'badge-neutral'}`}>
            {view.gaps.length}
          </span>
        </div>
        <div className="card-body">
          {view.gaps.length === 0 ? (
            <p className="muted">
              Nothing outstanding. Every applicable control is either met or not yet assessed.
            </p>
          ) : (
            <div className="eos-list">
              {view.gaps.map((r) => (
                <div className="eos-row" key={r.RequirementId}>
                  <div className="grow">
                    <div className="title">
                      <span className="mono xs subtle">{r.Code}</span> {r.Title}
                    </div>
                    {r.Notes && <div className="xs muted">{r.Notes}</div>}
                  </div>
                  <span
                    className={`eos-pill ${r.Status === 'not_met' ? 'critical' : 'serious'}`}
                  >
                    {STATUS_LABELS[r.Status]}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
